"use client"

import { useCallback, useEffect, useState } from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Box, Flex } from "@chakra-ui/react"

import Sidebar from "./Sidebar"
import TopBar from "./TopBar"
import NotificationPopup from "../NotificationPopup"
import NewDataPopup from "../newData/NewDataPopup"
import Footer from "../Footer"

import { useAuthentication } from "../../wrappers/UseAuthentication"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { usePageAccess } from "../../wrappers/PageAccessContext"
import { neutral, layout } from "../../theme/tokens"

const COLLAPSE_KEY = "spearbi_nav_collapsed"

// Pages where the retailer channel is not part of the question being asked.
// Showing the rail there implied the page was filtered by it.
const CHANNEL_FREE_ROUTES = [
  "/myProfile",
  "/users",
  "/addUser",
  "/loginhistory",
  "/supplierLogos",
  "/organisations",
  "/org-reports",
  "/editPageAccess",
  "/editSupplierAccess",
  "/editRetailerAccess",
]

const AuthenticatedLayout = () => {
  const { signOut } = useAuthentication()
  const { retailers, user_supplier_access } = useSupplierRetailer()
  const { user_page_access, loading: pageLoading, restrictedyn, trialyn, paymentReminder } = usePageAccess()
  const location = useLocation()

  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1")
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    const userid = localStorage.getItem("userid")
    setIsAdmin(userid === "1" || userid === "2")
  }, [])

  useEffect(() => {
    if (paymentReminder) setIsNotificationOpen(true)
  }, [paymentReminder])

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      localStorage.setItem(COLLAPSE_KEY, prev ? "0" : "1")
      return !prev
    })
  }, [])

  const showChannels =
    retailers.length > 0 && !CHANNEL_FREE_ROUTES.some((route) => location.pathname.startsWith(route))

  return (
    // No `overflow: hidden` on the shell — that is what would stop the command
    // bar's `position: sticky` from ever engaging.
    <Box bg={neutral[50]} minH="100vh">
      <NotificationPopup
        key={paymentReminder}
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        message={paymentReminder}
      />

      <NewDataPopup enabled={isAdmin} />

      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={toggleCollapse}
        userPageAccess={pageLoading ? [] : user_page_access}
        restrictedyn={restrictedyn}
        hasSuppliers={user_supplier_access.length > 0}
        isAdmin={isAdmin}
      />

      <Flex
        direction="column"
        minH="100vh"
        ml={collapsed ? layout.sidebarWidthCollapsed : layout.sidebarWidth}
        transition="margin-left 260ms cubic-bezier(0.2, 0, 0.38, 0.9)"
      >
        <TopBar onSignOut={signOut} trialyn={trialyn} showChannels={showChannels} />

        <Box as="main" flex="1" px={layout.contentGutter} py={4} minW={0}>
          <Outlet />
        </Box>

        <Footer />
      </Flex>
    </Box>
  )
}

export default AuthenticatedLayout
